import { apiClient } from "./api-client";
import type {
  AdminPaymentSettings,
  AdminDepositPayload,
  AdminDepositResponse,
  PayoutUserPayload,
  PayoutModeratorPayload,
  BackendPayoutResponse,
  BackendCreateRequestPayload,
  ValidateRequestPayload,
  ContributorAccumulatedAmount,
  ContributorPayoutSituation,
  ContributorTestSituation,
  ModeratorAccumulatedAmount,
  EligibleModeratorsResponse,
  AdminPayoutRequestsFullResponse,
  PayAllModeratorsResponse,
  BackendBudgetResponse,
  BackendPaymentStats,
  CommunityProfileAccumulatedAmount,
  CollectorPaymentDirection,
  CollectorPaymentHistoryResponse,
  CommunityProfilePaymentResult,
  PaymentNetwork,
} from '@/lib/types/payments';

// ============================================
// Helper Functions
// ============================================

const toNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

// ============================================
// PAYMENTS API
// ============================================

export const paymentsApi = {
  // ========== BUDGET ==========

  /**
   * Récupère le budget total et les statistiques
   * GET /payments/budget
   */
  async getBudget(): Promise<BackendBudgetResponse> {
    const { data } = await apiClient.get('/payments/budget');
    const budget = data?.budget ?? {};

    return {
      budget: toNumber(budget.total ?? data.total ?? 0),
      reserved: toNumber(budget.reserved ?? data.reserved ?? 0),
      available: toNumber(
        budget.available ?? data.available ?? Math.max(0, toNumber(budget.total ?? data.total ?? 0) - toNumber(budget.reserved ?? data.reserved ?? 0))
      ),
      currency: 'XOF',
      lastUpdated: new Date().toISOString(),
    };
  },

  /**
   * Récupère les statistiques de paiement
   * GET /payments/stats
   */
  async getPaymentStats(): Promise<BackendPaymentStats> {
    const { data } = await apiClient.get('/payments/stats');
    const stats = data?.stats ?? data ?? {};

    return {
      totalPaid: toNumber(stats.totalPaid ?? 0),
      totalPending: toNumber(stats.totalPending ?? 0),
      pendingTransactions: toNumber(stats.pendingTransactions ?? 0),
      pendingRequestsCount: toNumber(stats.pendingRequestsCount ?? stats.pendingTransactions ?? 0),
      pendingRequestsAmount: toNumber(stats.pendingRequestsAmount ?? stats.totalPending ?? 0),
      totalRequests: toNumber(stats.totalRequests ?? 0),
      balance: toNumber(stats.balance ?? 0),
      successfulTransactions: toNumber(stats.successfulTransactions ?? 0),
      failedTransactions: toNumber(stats.failedTransactions ?? 0),
    };
  },

  // ========== PAYMENT SETTINGS ==========

  /**
   * Récupère les paramètres de paiement (Admin only)
   * GET /payments/settings
   */
  async getPaymentSettings(): Promise<AdminPaymentSettings> {
    const { data } = await apiClient.get('/payments/settings');
    const settings = data?.settings ?? data ?? {};

    return {
      amountPerContribution: toNumber(settings.amountPerContribution ?? 6),
      contributorCap: toNumber(settings.contributorCap ?? 1000),
      moderatorAmount: toNumber(settings.moderatorAmount ?? 180000),
    };
  },

  /**
   * Met à jour les paramètres de paiement (Admin only)
   * POST /payments/settings
   */
  async updatePaymentSettings(dto: Partial<AdminPaymentSettings>): Promise<AdminPaymentSettings> {
    const { data } = await apiClient.post('/payments/settings', {
      amountPerContribution: dto.amountPerContribution,
      contributorCap: dto.contributorCap,
      moderatorAmount: dto.moderatorAmount,
    });
    const settings = data?.settings ?? data ?? {};

    return {
      amountPerContribution: toNumber(settings.amountPerContribution ?? 6),
      contributorCap: toNumber(settings.contributorCap ?? 1000),
      moderatorAmount: toNumber(settings.moderatorAmount ?? 180000),
    };
  },

  // ========== DEPOSITS ==========

  /**
   * Dépôt par admin via FeexPay
   * POST /payments/deposit
   */
  async depositAdmin(dto: AdminDepositPayload): Promise<AdminDepositResponse> {
    const { data } = await apiClient.post('/payments/deposit', {
      amount: dto.amount,
      phone: dto.phone,
      email: dto.email,
      firstName: dto.firstName,
      lastName: dto.lastName,
      description: dto.description,
    });

    return {
      success: data.success ?? true,
      txReference: data.txReference ?? data.transaction_id ?? '',
      identifier: data.identifier ?? data.merchant_transaction_id ?? '',
      payment_url: data.payment_url ?? '',
      message: data.message ?? 'Deposit initiated',
    };
  },

  // ========== PAYOUTS ==========

  /**
   * Paiement d'un contributeur par l'admin
   * POST /payments/payouts/user
   */
  async payoutUser(dto: PayoutUserPayload): Promise<BackendPayoutResponse> {
    const { data } = await apiClient.post('/payments/payouts/user', {
      userId: dto.userId,
      amount: dto.amount,
      phone: dto.phone,
      network: dto.network,
      description: dto.description,
    });

    return {
      success: data.success ?? true,
      tx_reference: data.txReference ?? data.transaction_id ?? '',
      amount: toNumber(data.amount),
      recipient: data.recipient ?? '',
      network: data.network ?? 'FLOOZ',
      status: data.status ?? 'PENDING',
      message: data.message ?? 'Payout initiated',
      createdAt: new Date().toISOString(),
    };
  },

  /**
   * Paiement d'un modérateur par l'admin
   * POST /payments/payouts/moderator
   */
  async payoutModerator(dto: PayoutModeratorPayload): Promise<BackendPayoutResponse> {
    const { data } = await apiClient.post('/payments/payouts/moderator', {
      moderatorId: dto.moderatorId,
      phone: dto.phone,
      network: dto.network,
      description: dto.description,
    });

    return {
      success: data.success ?? true,
      tx_reference: data.txReference ?? data.transaction_id ?? '',
      amount: toNumber(data.amount),
      recipient: data.recipient ?? '',
      network: data.network ?? 'FLOOZ',
      status: data.status ?? 'PENDING',
      message: data.message ?? 'Payout initiated',
      createdAt: new Date().toISOString(),
    };
  },

  /**
   * Paiement de tous les modérateurs
   * POST /payments/payouts/moderators/all
   */
  async payAllModerators(): Promise<PayAllModeratorsResponse> {
    const { data } = await apiClient.post('/payments/payouts/moderators/all');

    return {
      success: toNumber(data.success ?? 0),
      failed: toNumber(data.failed ?? 0),
      total: toNumber(data.total ?? 0),
    };
  },

  // ========== MODERATORS INFO ==========

  /**
   * Récupère les modérateurs éligibles
   * GET /payments/moderators/eligible
   */
  async getEligibleModerators(): Promise<EligibleModeratorsResponse> {
    const { data } = await apiClient.get('/payments/moderators/eligible');

    return {
      moderators: Array.isArray(data?.moderators)
        ? data.moderators.map((m: any) => ({
            id: toNumber(m.id),
            name: m.name ?? m.username ?? '',
            email: m.email ?? '',
            phoneNumber: m.phoneNumber ?? m.phone_number ?? '',
            validatedCount: toNumber(m.validatedCount ?? m.validated_count ?? 0),
          }))
        : [],
    };
  },

  /**
   * Récupère le montant accumulé d'un modérateur
   * GET /payments/moderator/:moderatorId/accumulated
   */
  async getModeratorAccumulatedAmount(moderatorId: number): Promise<ModeratorAccumulatedAmount> {
    const { data } = await apiClient.get(`/payments/moderator/${moderatorId}/accumulated`);

    return {
      validatedCount: toNumber(data.validatedCount ?? 0),
      accumulatedAmount: toNumber(data.accumulatedAmount ?? 0),
      moderatorAmount: toNumber(data.moderatorAmount ?? 0),
      isDue: Boolean(data.isDue ?? false),
      alreadyPaid: Boolean(data.alreadyPaid ?? false),
      hasOpenRequestOrTransaction: Boolean(data.hasOpenRequestOrTransaction ?? false),
      periodMonth: toNumber(data.periodMonth ?? 0),
      periodYear: toNumber(data.periodYear ?? 0),
    };
  },

  // ========== CONTRIBUTORS INFO ==========

  /**
   * Récupère le montant accumulé d'un contributeur
   * GET /payments/contributor/:userId/accumulated
   */
  async getContributorAccumulatedAmount(userId: number): Promise<ContributorAccumulatedAmount> {
    const { data } = await apiClient.get(`/payments/contributor/${userId}/accumulated`);

    return {
      acceptedAudioCount: toNumber(data.acceptedAudioCount ?? 0),
      payableCount: toNumber(data.payableCount ?? 0),
      contributorCap: toNumber(data.contributorCap ?? 0),
      amountPerContribution: toNumber(data.amountPerContribution ?? 0),
      accumulatedAmount: toNumber(data.accumulatedAmount ?? 0),
    };
  },

  /**
   * Récupère la situation de gain complète d'un contributeur (progression,
   * statut du test, baseline post-test, gains) — source de vérité de la carte
   * "Parcours de gain"
   * GET /payments/contributor/:userId/situation
   */
  async getContributorPayoutSituation(
    userId: number,
  ): Promise<ContributorPayoutSituation> {
    const { data } = await apiClient.get(
      `/payments/contributor/${userId}/situation`,
    );

    const tests = data?.tests ?? {};
    const normalizedTests: Record<string, ContributorTestSituation> = {};
    for (const [lang, test] of Object.entries(tests)) {
      normalizedTests[lang] = {
        status: test?.status ?? 'NONE',
        score: toNumber(test?.score),
      };
    }

    return {
      threshold: toNumber(data.threshold ?? 0),
      ratePerContribution: toNumber(data.ratePerContribution ?? 0),
      contributorCap: toNumber(data.contributorCap ?? 0),
      acceptedAudioCount: toNumber(data.acceptedAudioCount ?? 0),
      totalUploaded: toNumber(data.totalUploaded ?? 0),
      step1Reached: Boolean(data.step1Reached),
      step1Remaining: toNumber(data.step1Remaining ?? 0),
      step1Progress: toNumber(data.step1Progress ?? 0),
      tests: normalizedTests,
      hasValidatedTest: Boolean(data.hasValidatedTest),
      baselineAccepted:
        data.baselineAccepted === null || data.baselineAccepted === undefined
          ? null
          : toNumber(data.baselineAccepted),
      validatedAudiosAfterTest: toNumber(data.validatedAudiosAfterTest ?? 0),
      payableCount: toNumber(data.payableCount ?? 0),
      accumulatedAmount: toNumber(data.accumulatedAmount ?? 0),
      awaitingPayoutAmount: toNumber(data.awaitingPayoutAmount ?? 0),
      earnedAmount: toNumber(data.earnedAmount ?? 0),
      canRequestPayout: Boolean(data.canRequestPayout),
      hasPendingPayoutRequest: Boolean(data.hasPendingPayoutRequest),
      latestPayoutStatus: data.latestPayoutStatus ?? null,
    };
  },

  // ========== COMMUNITY PROFILE PAYMENT (mobile money) ==========

  /**
   * Récupère le montant à payer pour un community profile
   * GET /payments/community-profile/:id/accumulated
   */
  /**
   * Historique des paiements du collecteur connecté
   * GET /payments/collector/me/history
   */
  async getCollectorPaymentHistory(
    direction: CollectorPaymentDirection,
    page = 1,
    limit = 10,
  ): Promise<CollectorPaymentHistoryResponse> {
    const { data } = await apiClient.get(`/payments/collector/me/history`, {
      params: { direction, page, limit },
    });

    return {
      total: toNumber(data.total ?? 0),
      page: toNumber(data.page ?? page),
      limit: toNumber(data.limit ?? limit),
      totalPages: Math.max(1, toNumber(data.totalPages ?? 1)),
      data: Array.isArray(data.data)
        ? data.data.map((item: Record<string, unknown>) => ({
            id: toNumber(item.id ?? 0),
            amount: toNumber(item.amount ?? 0),
            amountPaid: item.amountPaid == null ? null : toNumber(item.amountPaid),
            status: String(item.status ?? ''),
            network: (item.network as string | undefined) ?? null,
            phoneNumber: (item.phoneNumber as string | undefined) ?? null,
            profileCode: (item.profileCode as string | undefined) ?? null,
            contributionsPaid:
              item.contributionsPaid == null ? null : toNumber(item.contributionsPaid),
            createdAt: String(item.createdAt ?? ''),
            processedAt: item.processedAt == null ? null : String(item.processedAt),
          }))
        : [],
    };
  },

  async getCommunityProfileAccumulated(profileId: number): Promise<CommunityProfileAccumulatedAmount> {
    const { data } = await apiClient.get(`/payments/community-profile/${profileId}/accumulated`);

    return {
      profileId: toNumber(data.profileId ?? profileId),
      profileCode: data.profileCode ?? '',
      totalCount: toNumber(data.totalCount ?? 0),
      pendingCount: toNumber(data.pendingCount ?? 0),
      rejectedCount: toNumber(data.rejectedCount ?? 0),
      acceptedCount: toNumber(data.acceptedCount ?? 0),
      alreadyPaidCount: toNumber(data.alreadyPaidCount ?? 0),
      payableCount: toNumber(data.payableCount ?? 0),
      amount: toNumber(data.amount ?? 0),
      phoneNumber: data.phoneNumber ?? null,
      network: (data.network as PaymentNetwork | undefined) ?? null,
    };
  },

  /**
   * Déclenche le paiement mobile money d'un community profile
   * POST /payments/community-profile/:id/pay
   */
  async payCommunityProfile(profileId: number): Promise<CommunityProfilePaymentResult> {
    const { data } = await apiClient.post(`/payments/community-profile/${profileId}/pay`);

    return {
      success: data.success ?? true,
      txReference: data.txReference ?? '',
      identifier: data.identifier ?? '',
      amount: toNumber(data.amount ?? 0),
      contributionsPaid: toNumber(data.contributionsPaid ?? 0),
    };
  },

  // ========== PAYOUT REQUESTS ==========

  /**
   * Crée une demande de paiement (par utilisateur)
   * POST /payments/requests
   */
  async createPayoutRequest(dto: BackendCreateRequestPayload): Promise<any> {
    const { data } = await apiClient.post('/payments/requests', {
      amount: dto.amount,
      phone: dto.phone,
      network: dto.network,
      description: dto.description,
    });

    return {
      success: data.success ?? true,
      id: data.id ?? '',
      amount: toNumber(data.amount),
      maxAmount: toNumber(data.maxAmount ?? 0),
      periodMonth: toNumber(data.periodMonth ?? 0),
      periodYear: toNumber(data.periodYear ?? 0),
    };
  },

  /**
   * Récupère les demandes de paiement filtrées
   * GET /payments/requests
   */
  async getPayoutRequests(status?: string): Promise<any> {
    const { data } = await apiClient.get('/payments/requests', {
      params: { status },
    });

    return {
      items: Array.isArray(data?.items) ? data.items : [],
    };
  },

  /**
   * Récupère les demandes de paiement de l'utilisateur connecté
   * GET /payments/requests/me
   */
  async getUserPayoutRequests(): Promise<any> {
    const { data } = await apiClient.get('/payments/requests/me');

    return {
      items: Array.isArray(data?.items) ? data.items : [],
    };
  },

  /**
   * Récupère les demandes de paiement pour l'admin (paginées)
   * GET /payments/admin/requests
   */
  async getAdminPayoutRequests(moderatorPage = 1, contributorPage = 1): Promise<AdminPayoutRequestsFullResponse> {
    const { data } = await apiClient.get('/payments/admin/requests', {
      params: { modPage: moderatorPage, contPage: contributorPage },
    });

    const moderators = data?.moderators ?? {};
    const contributors = data?.contributors ?? {};
    const summary = data?.summary ?? {};

    return {
      moderators: {
        items: Array.isArray(moderators.items) ? moderators.items : [],
        total: toNumber(moderators.total ?? 0),
        page: toNumber(moderators.page ?? 1),
        pageSize: toNumber(moderators.pageSize ?? 10),
        totalPages: toNumber(moderators.totalPages ?? 0),
      },
      contributors: {
        items: Array.isArray(contributors.items) ? contributors.items : [],
        total: toNumber(contributors.total ?? 0),
        page: toNumber(contributors.page ?? 1),
        pageSize: toNumber(contributors.pageSize ?? 10),
        totalPages: toNumber(contributors.totalPages ?? 0),
      },
      summary: {
        totalPending: toNumber(summary.totalPending ?? 0),
        totalModeratorsPending: toNumber(summary.totalModeratorsPending ?? 0),
        totalContributorsPending: toNumber(summary.totalContributorsPending ?? 0),
        totalModeratorsAmount: toNumber(summary.totalModeratorsAmount ?? 0),
        totalContributorsAmount: toNumber(summary.totalContributorsAmount ?? 0),
      },
    };
  },

  /**
   * Valide une demande de paiement (modérateur valide)
   * POST /payments/requests/:id/validate
   */
  async validatePayoutRequest(requestId: number, dto: ValidateRequestPayload): Promise<any> {
    const { data } = await apiClient.post(`/payments/requests/${requestId}/validate`, {
      action: dto.action,
      note: dto.note,
    });

    return {
      success: data.success ?? true,
      status: data.status ?? dto.action,
    };
  },

  /**
   * Approuve une demande de paiement (admin)
   * POST /payments/admin/requests/:id/approve
   */
  async approvePayoutRequest(requestId: number, note?: string): Promise<any> {
    const { data } = await apiClient.post(`/payments/admin/requests/${requestId}/approve`, {
      note,
    });

    return {
      success: data.success ?? true,
      status: data.status ?? 'APPROVED',
    };
  },

  /**
   * Rejette une demande de paiement (admin)
   * POST /payments/admin/requests/:id/reject
   */
  async rejectPayoutRequest(requestId: number, note?: string): Promise<any> {
    const { data } = await apiClient.post(`/payments/admin/requests/${requestId}/reject`, {
      note,
    });

    return {
      success: data.success ?? true,
      status: data.status ?? 'REJECTED',
    };
  },

  /**
   * Marque une demande comme payée (initie le paiement)
   * POST /payments/requests/:id/paid
   */
  async markRequestAsPaid(requestId: number): Promise<any> {
    const { data } = await apiClient.post(`/payments/requests/${requestId}/paid`);

    return {
      success: data.success ?? true,
      txReference: data.txReference ?? data.transaction_id ?? '',
      identifier: data.identifier ?? data.merchant_transaction_id ?? '',
    };
  },

};

// Export l'API pour utilisation dans les composants
export default paymentsApi;
